import React from 'react';
import { motion } from 'framer-motion';
import Typewriter from 'typewriter-effect';
import { BinaryBackground } from '../components/BinaryBackground';

export const About = () => {
  return (
    <>
      <BinaryBackground />
      
      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-6xl font-bold text-cyan-400 neon-text mb-6">
              <Typewriter
                options={{
                  strings: ['About HyperMesh AI'],
                  autoStart: true,
                  loop: false,
                  cursor: '_',
                  delay: 50,
                  deleteSpeed: 9999999 // Effectively prevents deletion
                }}
              />
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Empowering businesses with AI-driven automation. The future is here.
            </p>
            <p className="text-lg text-gray-400">
              HyperMesh AI is an automation-first company helping businesses integrate AI solutions that drive efficiency, boost revenue, and scale faster. From AI-powered customer support to lead generation and appointment setting, we bring cutting-edge AI technology to your business.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Who We Are Section */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="py-20 px-4"
      >
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-cyan-400 neon-text mb-8">Who We Are</h2>
          <div className="space-y-6 text-gray-300">
            <p>
              HyperMesh AI is more than just an AI automation agency. We are builders, innovators, and problem-solvers who believe in the power of artificial intelligence to redefine the way businesses operate.
            </p>
            <p>
              Founded by Hamza, an AI enthusiast and entrepreneur with a deep understanding of automation and digital transformation, HyperMesh AI was built to bridge the gap between businesses and cutting-edge AI solutions. Most companies want to leverage AI but don't know how, and that's where we come in.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Why AI Section */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="py-20 px-4 bg-black/50"
      >
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-cyan-400 neon-text mb-8">Why AI? Why Now?</h2>
          <div className="space-y-8">
            <p className="text-gray-300">
              The world is evolving faster than ever. Businesses that fail to integrate AI into their operations risk falling behind. At HyperMesh AI, we believe that automation isn't just about efficiency—it's about unlocking new opportunities.
            </p>
            <ul className="space-y-4 text-gray-300">
              <li className="flex items-start gap-2">
                <span className="text-cyan-400">→</span>
                Customer expectations are rising. Faster responses, smarter interactions, and 24/7 availability are now essential.
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyan-400">→</span>
                Manual processes slow businesses down. AI can streamline workflows, reduce costs, and eliminate human error.
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyan-400">→</span>
                Competition is fierce. AI-driven businesses scale faster, make data-driven decisions, and outperform their competitors.
              </li>
            </ul>
          </div>
        </div>
      </motion.section>

      {/* Mission & Vision Section */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="py-20 px-4"
      >
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-cyan-400 neon-text mb-8">Our Mission & Vision</h2>
          <div className="space-y-8">
            <div className="service-card p-6">
              <h3 className="text-xl font-semibold text-cyan-300 mb-4">Mission</h3>
              <p className="text-gray-300">
                "To make AI automation accessible to businesses of all sizes, empowering them to scale faster and operate smarter."
              </p>
            </div>
            <div className="service-card p-6">
              <h3 className="text-xl font-semibold text-cyan-300 mb-4">Vision</h3>
              <p className="text-gray-300">
                We envision a world where AI automation is the backbone of every successful company, where businesses run smarter, scale faster, and serve customers better than ever before.
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* What Makes Us Different Section */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="py-20 px-4 bg-black/50"
      >
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-cyan-400 neon-text mb-8">What Makes Us Different</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                title: "Solution Builders",
                description: "We don't just sell AI—we build solutions. Every tool we develop is designed with a real business problem in mind."
              },
              {
                title: "Results Focused",
                description: "We focus on results, not just technology. Our goal isn't to overwhelm businesses with AI jargon, but to deliver measurable growth."
              },
              {
                title: "Collaborative Approach",
                description: "We value collaboration. We work closely with our clients to customize AI solutions that fit their unique needs."
              },
              {
                title: "User-Centric Design",
                description: "Technology should work for you, not against you. That's why our AI solutions are easy to integrate, cost-effective, and built to scale."
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="service-card p-6"
              >
                <h3 className="text-xl font-semibold text-cyan-300 mb-4">{item.title}</h3>
                <p className="text-gray-300">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="py-20 px-4"
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-cyan-400 neon-text mb-8">Ready to Transform Your Business?</h2>
          <a
            href="/contact"
            className="cta-button inline-block text-lg font-semibold"
          >
            &gt; Work With Us
          </a>
        </div>
      </motion.section>
    </>
  );
};